import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';

import { fetchExtension } from 'flow/actions/extension-actions';
import LiveChat from 'flow/components/ProfilePicker/LiveChats';
import Profiles, {
    ProfileToFocus,
} from 'flow/components/ProfilePicker/Profiles';
import Queues from 'flow/components/ProfilePicker/Queues';
import StatusInput from 'flow/components/ProfilePicker/StatusInput';
import useAppContext from 'flow/hooks/useAppContext';
import useProfiles from 'flow/hooks/useProfiles';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { ExtensionKey } from 'flow/types/EntityKeys';
import Tabs from '../ui-components/Tabs';

interface Props {
    enableLogInOptions: boolean;
    showSettingsLink: boolean;
    extensionKey: ExtensionKey;
    profileToFocus?: ProfileToFocus;
    onProfileToFocusHandled?: () => void;
}

const ProfilePickerMenu = ({
    enableLogInOptions,
    showSettingsLink,
    extensionKey = null,
    profileToFocus = null,
    onProfileToFocusHandled,
}: Props) => {
    const { i18next } = useAppContext();

    const dispatch = useAppDispatch();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const [tab, setTab] = useState<string | number>('profiles');

    const key = extensionKey || ownExtensionKey;

    useEffect(() => {
        setTimeout(() => dispatch(fetchExtension(key, 'live')), 300);
    }, [dispatch, key, open]);

    const { sortedProfiles } = useProfiles(extensionKey || null);

    // Only the Profiles tab can take a request to focus a profile. One made
    // while the Log in tab shows is dropped, so it does not wait for the next
    // time the profiles show.
    useEffect(() => {
        if (profileToFocus && tab !== 'profiles') {
            onProfileToFocusHandled?.();
        }
    }, [profileToFocus, tab, onProfileToFocusHandled]);

    const tabs = [
        { key: 'profiles', label: i18next.t('profilechooser.profiles') },
        { key: 'login', label: i18next.t('login') },
    ];

    return (
        <>
            <div className="px-3">
                <StatusInput extensionKey={key} />
            </div>

            {enableLogInOptions ? (
                <div className="my-3 px-3">
                    <Tabs
                        className="h-8"
                        tabs={tabs}
                        onClick={({ key }) => setTab(key)}
                        tabClassName="bg-gray-300"
                    />
                </div>
            ) : null}

            {tab === 'profiles' ? (
                <Profiles
                    extensionKey={key}
                    profiles={sortedProfiles}
                    showSettingsLink={showSettingsLink}
                    profileToFocus={profileToFocus}
                    onProfileToFocusHandled={onProfileToFocusHandled}
                />
            ) : (
                <div className="scrollbar max-h-[360px] overflow-y-scroll px-3">
                    <Queues />
                    <LiveChat />
                </div>
            )}
        </>
    );
};

export default ProfilePickerMenu;
